// screens/FlightResultsScreen.js
import React, { useState, useEffect, useContext } from 'react';
import { 
  useNavigate, 
  useLocation,
  Link,
  useParams
} from 'react-router-dom';
import { 
  ArrowLeft, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  RefreshCw,
  X,
  Clock,
  Users,
  Calendar,
  Plane,
  Shield,
  Wifi,
  Coffee,
  Luggage,
  Star,
  ExternalLink
} from 'lucide-react';
import { AuthContext } from '../../../../Context/AuthContext';
import FlightService from './FlightService';

// ======================================================
// EXPEDIA REDIRECT UTILITY FUNCTIONS
// ======================================================

// Extract 3-letter airport code from various formats
const extractAirportCode = (input) => {
  if (!input) return '';
  
  // Handle object with code property
  if (typeof input === 'object' && input.code) {
    return input.code.toUpperCase();
  }
  
  // If it's already a 3-letter code, return it uppercase
  if (typeof input === 'string' && input.length === 3 && /^[A-Z]{3}$/.test(input.toUpperCase())) {
    return input.toUpperCase();
  }
  
  // Try to extract code from string like "DUBAI (DXB) - INTERNATIONAL"
  const match = String(input).match(/\(([A-Z]{3})\)/);
  if (match && match[1]) {
    return match[1];
  }
  
  // Handle string like "JFK - John F. Kennedy International"
  const codeMatch = String(input).match(/^([A-Z]{3})/);
  if (codeMatch && codeMatch[1]) {
    return codeMatch[1];
  }
  
  // Return original string uppercased if no pattern matched
  return String(input).toUpperCase().substring(0, 3);
};

// Format date for URL (YYYY-MM-DD)
const formatDateForURL = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

// Add currency parameter to existing URL
const addCurrencyToUrl = (url, currency) => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    
    // Add multiple currency parameter variations
    if (!params.has('currency')) params.append('currency', currency);
    if (!params.has('selectedCurrency')) params.append('selectedCurrency', currency);
    if (!params.has('preferredCurrency')) params.append('preferredCurrency', currency);
    if (!params.has('curr')) params.append('curr', currency);
    if (!params.has('currencyCode')) params.append('currencyCode', currency);
    
    // Add market and locale parameters
    if (!params.has('market')) params.append('market', 'global');
    if (!params.has('locale')) params.append('locale', getLocaleFromCurrency(currency));
    
    urlObj.search = params.toString();
    return urlObj.toString();
  } catch (error) {
    console.error('Error adding currency to URL:', error);
    return url;
  }
};

// Get locale from currency code
const getLocaleFromCurrency = (currency) => {
  const localeMap = {
    'USD': 'en-US',
    'EUR': 'en-GB',
    'GBP': 'en-GB',
    'JPY': 'ja-JP',
    'CNY': 'zh-CN',
    'INR': 'en-IN',
    'AUD': 'en-AU',
    'CAD': 'en-CA',
    'CHF': 'de-CH',
    'SGD': 'en-SG',
    'AED': 'en-AE',
    'SAR': 'ar-SA',
    'KRW': 'ko-KR',
    'MXN': 'es-MX',
    'BRL': 'pt-BR',
    'ZAR': 'en-ZA',
    'THB': 'th-TH',
    'VND': 'vi-VN',
    'IDR': 'id-ID',
    'MYR': 'ms-MY',
    'PHP': 'en-PH',
    'PKR': 'ur-PK',
    'BDT': 'bn-BD',
    'LKR': 'si-LK',
    'NPR': 'ne-NP',
    'MMK': 'my-MM',
    'UAH': 'uk-UA',
    'PLN': 'pl-PL',
    'CZK': 'cs-CZ',
    'SEK': 'sv-SE',
    'NOK': 'no-NO',
    'DKK': 'da-DK',
    'RUB': 'ru-RU',
    'TRY': 'tr-TR',
    'HKD': 'zh-HK',
    'NZD': 'en-NZ',
    'ILS': 'he-IL',
    'EGP': 'ar-EG',
    'IQD': 'ar-IQ',
    'KWD': 'ar-KW',
    'OMR': 'ar-OM',
    'QAR': 'ar-QA',
    'BHD': 'ar-BH'
  };
  
  return localeMap[currency] || 'en-US';
};

// Enhanced URL construction with better Expedia currency support
const constructExpediaDeepLink = (flight, currency = 'USD') => {
  try {
    const departureCode = extractAirportCode(flight.departure?.code || flight.departureAirport);
    const arrivalCode = extractAirportCode(flight.destination?.code || flight.arrivalAirport);
    const departureDate = formatDateForURL(flight.departure?.time || flight.departureTime);
    const returnDate = flight.returnDate ? formatDateForURL(flight.returnDate) : null;
    const tripType = flight.tripType || 'roundTrip';
    
    if (!departureCode || !arrivalCode || !departureDate) {
      ////console.log('Missing required flight data');
      return null;
    }

    // Build Expedia URL with comprehensive currency parameters
    const baseParams = {
      trip: tripType === 'oneWay' ? 'oneway' : 'roundtrip',
      leg1: `${departureCode},${arrivalCode},${departureDate}`,
      passengers: `adults:${flight.travelerCount?.adults || 1},children:${flight.travelerCount?.children || 0},infantinlap:${flight.travelerCount?.infants || 0}`,
      options: 'cabinclass:economy',
      mode: 'search'
    };

    // Add return leg for round trips
    if (tripType !== 'oneWay' && returnDate) {
      baseParams.leg2 = `${arrivalCode},${departureCode},${returnDate}`;
    }

    // Create URL with comprehensive currency parameter variations
    const params = new URLSearchParams(baseParams);
    
    // Add multiple currency parameters that Expedia recognizes
    params.append('currency', currency);
    params.append('selectedCurrency', currency);
    params.append('preferredCurrency', currency);
    params.append('curr', currency); 
    params.append('currencyCode', currency); 
    
    // Add market and locale parameters for better currency support
    params.append('market', 'global');
    params.append('locale', getLocaleFromCurrency(currency));
    
    const expediaUrl = `https://www.expedia.com/Flights-Search?${params.toString()}`;
    
    return expediaUrl;

  } catch (error) {
    console.error('Error constructing Expedia URL:', error);
    return null;
  }
};

// Main handler for booking on Expedia
const handleBookOnExpedia = (flight, currency = 'USD') => {
  // If flight has a direct link and it's not the homepage, use it
  if (flight.link && !flight.link.includes('expedia.com/home')) {
    // Enhance the existing link with project currency parameter
    let enhancedUrl = addCurrencyToUrl(flight.link, currency);
    
    // Open in new tab
    window.open(enhancedUrl, '_blank', 'noopener,noreferrer');
    return;
  } else {
    // Construct a new Expedia URL with project currency
    const expediaUrl = constructExpediaDeepLink(flight, currency);
    
    if (expediaUrl) {
      // Open in new tab
      window.open(expediaUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert('Booking link not available for this flight');
    }
  }
};

const FlightList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { frondId } = useParams();
  const { token } = useContext(AuthContext);
  const flightService = FlightService;
  
  // Get search params from location state or URL
  const searchParams = location.state?.searchParams || {};
  const from = location.state?.from || searchParams.departureCode || '';
  const to = location.state?.to || searchParams.destinationCode || '';
  
  // State
  const [flights, setFlights] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters state
  const [filters, setFilters] = useState({
    stops: 'all', // 'all', 'direct', '1stop', '2plus'
    sortBy: 'price', // 'price', 'duration', 'departure', 'arrival'
    priceRange: [0, 5000],
    airlines: [],
    cabinClass: 'all' // 'all', 'economy', 'premium', 'business', 'first'
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  
  // Helper function to parse price string to number
  const parsePrice = (priceValue) => {
    if (typeof priceValue === 'number') return priceValue;
    if (typeof priceValue === 'string') {
      // Extract numeric part from string like "1063.36 USD" or "$1,234.56"
      const numericPart = priceValue.replace(/[^0-9.]/g, '');
      const parsed = parseFloat(numericPart);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  // Helper function to parse duration string to minutes
  const parseDurationToMinutes = (duration) => {
    if (!duration) return 0;
    
    // Handle string format like "19h25m"
    if (typeof duration === 'string') {
      const hoursMatch = duration.match(/(\d+)h/);
      const minsMatch = duration.match(/(\d+)m/);
      const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
      const mins = minsMatch ? parseInt(minsMatch[1], 10) : 0;
      return (hours * 60) + mins;
    }
    
    // Handle ISO 8601 format like "PT19H25M"
    if (typeof duration === 'string' && duration.startsWith('PT')) {
      const hoursMatch = duration.match(/(\d+)H/);
      const minsMatch = duration.match(/(\d+)M/);
      const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
      const mins = minsMatch ? parseInt(minsMatch[1], 10) : 0;
      return (hours * 60) + mins;
    }
    
    // If already a number (minutes), return as-is
    if (typeof duration === 'number') return duration;
    
    return 0;
  };

  // Fetch flights on mount
  useEffect(() => {
    if (Object.keys(searchParams).length > 0) {
      fetchFlights();
    } else {
      setError('No search parameters provided');
      setLoading(false);
    }
  }, [searchParams]);

  const fetchFlights = async () => {
    try {
      setLoading(true);
      setError(null);

      //console.log('🔍 Search Params:', searchParams);
    //console.log('🔑 Token present:', !!token);
    
      
      const results = await flightService.searchFlights(searchParams);
      
      // Normalize flight data - parse price strings to numbers and map API fields to component format
      const normalizedResults = results.map((flight, index) => {
        // Extract airport codes from the name (e.g., "TUU to OSL • 1 stop")
        const nameMatch = flight.name?.match(/([A-Z]{3})\s+to\s+([A-Z]{3})/);
        const outboundCode = nameMatch?.[1] || 'TUU';
        const inboundCode = nameMatch?.[2] || 'OSL';
        
        return {
          ...flight,
          id: flight.id || `flight-${index}`,
          // Convert price string to number
          price: parsePrice(flight.price),
          // Map flat API fields to nested structure expected by FlightCard
          departure: {
            time: flight.departureTime,
            code: outboundCode,
            name: `${outboundCode} Airport`
          },
          destination: {
            time: flight.arriveTime,
            code: inboundCode,
            name: `${inboundCode} Airport`
          },
          // Map stops - use outboundStops from API
          stops: flight.outboundStops || 0,
          // Map duration - handle both string format "19h25m" and minutes
          durationMinutes: parseDurationToMinutes(flight.duration),
          // Ensure airline and other fields are present
          airline: flight.airline || 'Unknown',
          flightNumber: flight.airline ? `${flight.airline}${Math.floor(Math.random() * 9000) + 1000}` : 'Unknown',
          currency: flight.currency || 'USD',
          rating: flight.rating || '4.5',
          // Return trip info
          returnDeparture: {
            time: flight.returnDepartureTime,
            code: inboundCode,
            name: `${inboundCode} Airport`
          },
          returnArrival: {
            time: flight.returnArriveTime,
            code: outboundCode,
            name: `${outboundCode} Airport`
          },
          returnStops: flight.returnStops || 0
        };
      });
      
      setFlights(normalizedResults);
      setFilteredFlights(normalizedResults);
      
      // Extract unique airlines for filter
      const uniqueAirlines = [...new Set(normalizedResults.map(flight => flight.airline))]
        .filter(Boolean)
        .map(airline => ({ 
          name: airline, 
          selected: false,
          logo: getAirlineLogo(airline)
        }));
      
      // Extract max price for range (use parsed numeric values)
      const maxPrice = Math.max(...normalizedResults.map(f => f.price || 0), 50000);

       //console.log('📦 API Results:', results);
    //console.log('📊 Results type:', typeof results);
    //console.log('📊 Is array?', Array.isArray(results));
    //console.log('📊 Length:', results?.length || 0);
      
      setFilters(prev => ({
        ...prev,
        airlines: uniqueAirlines,
        priceRange: [0, maxPrice]
      }));
      
    } catch (err) {
      console.error('Flight search error:', err);
      setError(err.message || 'Failed to fetch flights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    if (flights.length === 0) return;
    
    let filtered = [...flights];
    
    // Filter by stops
    if (filters.stops !== 'all') {
      filtered = filtered.filter(flight => {
        if (filters.stops === 'direct') return flight.stops === 0;
        if (filters.stops === '1stop') return flight.stops === 1;
        if (filters.stops === '2plus') return flight.stops >= 2;
        return true;
      });
    }
    
    // Filter by selected airlines
    if (filters.airlines.some(airline => airline.selected)) {
      const selectedAirlineNames = filters.airlines
        .filter(airline => airline.selected)
        .map(airline => airline.name);
      
      filtered = filtered.filter(flight => 
        selectedAirlineNames.includes(flight.airline)
      );
    }
    
    // Filter by price range
    filtered = filtered.filter(flight => 
      flight.price >= filters.priceRange[0] && 
      flight.price <= filters.priceRange[1]
    );
    
    // Filter by cabin class
    if (filters.cabinClass !== 'all') {
      filtered = filtered.filter(flight => 
        flight.cabinClass?.toLowerCase() === filters.cabinClass.toLowerCase()
      );
    }
    
    // Sort results
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'price':
          return a.price - b.price;
        case 'duration':
          return (a.durationMinutes || 0) - (b.durationMinutes || 0);
        case 'departure':
          return new Date(a.departure.time) - new Date(b.departure.time);
        case 'arrival':
          return new Date(a.destination.time) - new Date(b.destination.time);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        default:
          return 0;
      }
    });
    
    setFilteredFlights(filtered);
  }, [filters, flights]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const toggleAirlineFilter = (airlineName) => {
    setFilters(prev => ({
      ...prev,
      airlines: prev.airlines.map(airline => 
        airline.name === airlineName 
          ? { ...airline, selected: !airline.selected }
          : airline
      )
    }));
  };

  const resetFilters = () => {
    // Use already parsed numeric price values
    const maxPrice = Math.max(...flights.map(f => f.price || 0), 50000);
    
    setFilters({
      stops: 'all',
      sortBy: 'price',
      priceRange: [0, maxPrice],
      airlines: filters.airlines.map(airline => ({ ...airline, selected: false })),
      cabinClass: 'all'
    });
  };

  // Helper functions
  const getAirlineLogo = (airlineName) => {
    const logos = {
      'Delta': '/airlines/delta.png',
      'United': '/airlines/united.png',
      'American': '/airlines/american.png',
      'Emirates': '/airlines/emirates.png',
      'British Airways': '/airlines/ba.png',
      'Air France': '/airlines/airfrance.png',
      'Lufthansa': '/airlines/lufthansa.png',
      'Qatar Airways': '/airlines/qatar.png',
      'Singapore Airlines': '/airlines/singapore.png'
    };
    
    return logos[airlineName] || '/airlines/default.png';
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '--h --m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatPrice = (price, currency = 'USD') => {
    // Handle both string and number inputs
    const numericPrice = parsePrice(price);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(numericPrice);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render flight card
  const FlightCard = ({ flight }) => (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 hover:border-blue-300 transition-all duration-300 hover:shadow-xl overflow-hidden">
      {/* Flight Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
              <Plane className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-gray-900">{flight.airline}</h3>
              <p className="text-sm text-gray-500">Flight {flight.flightNumber}</p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">
              {formatPrice(flight.price, flight.currency)}
            </div>
            <p className="text-sm text-gray-500">per person</p>
          </div>
        </div>
      </div>

      {/* Flight Route */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          {/* Departure */}
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{formatTime(flight.departure?.time)}</div>
            <div className="text-sm text-gray-500 mt-1">{flight.departure?.code}</div>
            <div className="text-xs text-gray-400 mt-1 truncate max-w-[120px]">
              {flight.departure?.name || 'Departure'}
            </div>
          </div>

          {/* Middle section */}
          <div className="flex-1 px-4">
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                <Clock className="w-4 h-4" />
                {formatDuration(flight.durationMinutes)}
              </div>
            </div>
            <div className="relative h-2">
              <div className="absolute inset-0 flex items-center">
                <div className="flex-1 border-t border-dashed border-gray-300"></div>
              </div>
              <div className="absolute left-0 w-3 h-3 rounded-full bg-blue-500"></div>
              <div className="absolute right-0 w-3 h-3 rounded-full bg-blue-500"></div>
            </div>
            <div className="text-center mt-2">
              <span className="text-sm text-gray-500">
                {flight.stops === 0 
                  ? 'Direct flight' 
                  : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`
                }
              </span>
            </div>
          </div>

          {/* Arrival */}
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{formatTime(flight.destination?.time)}</div>
            <div className="text-sm text-gray-500 mt-1">{flight.destination?.code}</div>
            <div className="text-xs text-gray-400 mt-1 truncate max-w-[120px]">
              {flight.destination?.name || 'Destination'}
            </div>
          </div>
        </div>
      </div>

      {/* Flight Amenities */}
      <div className="px-6 pb-4">
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Luggage className="w-4 h-4" />
            <span>{flight.baggage || 'Cabin: 7kg'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Coffee className="w-4 h-4" />
            <span>{flight.meal ? 'Meal included' : 'Meal available'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Wifi className="w-4 h-4" />
            <span>{flight.wifi ? 'Free WiFi' : 'WiFi available'}</span>
          </div>
          {flight.rating && (
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span>{flight.rating}/5</span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
<div className="px-6 pb-6">
  <div className="grid grid-cols-2 gap-3">
    <Link
      to={`/frond/discover/${frondId}/flight-details/${flight.id}`}
      state={{ flight, searchParams }}
      className="bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold text-center hover:bg-blue-700 transition-colors duration-300"
    >
      View Details
    </Link>
    <button
      onClick={() => handleBookOnExpedia(flight, flight.currency)}
      className="border-2 border-blue-600 text-blue-600 py-3 px-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center gap-2"
    >
      <ExternalLink className="w-4 h-4" />
      Book on Expedia
    </button>
  </div>
</div>
    </div>
  );

  // Render filters panel
  const FiltersPanel = ({ isMobile = false }) => (
    <div className={`bg-white rounded-2xl shadow-lg border border-gray-200 ${isMobile ? 'h-full overflow-y-auto' : ''}`}>
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-gray-900">Filters</h3>
          <button
            onClick={resetFilters}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <RefreshCw className="w-4 h-4" />
            Reset all
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Stops Filter */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Stops</h4>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'all', label: 'All flights' },
              { value: 'direct', label: 'Direct' },
              { value: '1stop', label: '1 Stop' },
              { value: '2plus', label: '2+ Stops' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange('stops', option.value)}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  filters.stops === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sort Filter */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Sort by</h4>
          <div className="space-y-2">
            {[
              { value: 'price', label: 'Price (Lowest first)' },
              { value: 'duration', label: 'Duration (Shortest first)' },
              { value: 'departure', label: 'Departure Time' },
              { value: 'arrival', label: 'Arrival Time' },
              { value: 'rating', label: 'Highest Rating' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange('sortBy', option.value)}
                className={`w-full py-2 px-3 rounded-lg text-sm font-medium text-left transition-colors ${
                  filters.sortBy === option.value
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Airlines Filter */}
        {filters.airlines.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Airlines</h4>
            <div className="space-y-2">
              {filters.airlines.slice(0, 5).map((airline) => (
                <button
                  key={airline.name}
                  onClick={() => toggleAirlineFilter(airline.name)}
                  className={`w-full py-2 px-3 rounded-lg text-sm font-medium text-left transition-colors flex items-center gap-3 ${
                    airline.selected
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded flex items-center justify-center">
                    <Plane className="w-3 h-3 text-blue-600" />
                  </div>
                  <span>{airline.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Cabin Class Filter */}
        {/* <div>
          <h4 className="font-semibold text-gray-900 mb-3">Cabin Class</h4>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'economy', label: 'Economy' },
              { value: 'premium', label: 'Premium' },
              { value: 'business', label: 'Business' },
              { value: 'first', label: 'First Class' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange('cabinClass', option.value)}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  filters.cabinClass === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))} */}
          {/* </div> */}
        {/* </div> */}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <Plane className="absolute inset-0 m-auto w-8 h-8 text-blue-500 animate-pulse" />
          </div>
          <p className="mt-4 text-lg text-gray-600">Searching for flights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Flights</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={fetchFlights}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Back to Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Mobile Filters Overlay */}
      {showMobileFilters && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 lg:hidden">
          <div className="absolute inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-lg">Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <FiltersPanel isMobile />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Flight Results</h1>
                <p className="text-blue-200">
                  {from} to {to} • {formatDate(searchParams.departureDate)}
                </p>
              </div>
            </div>
            
            {/* Desktop Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="hidden lg:flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowMobileFilters(true)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-full"
            >
              <Filter className="w-6 h-6" />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-sm text-blue-200 mb-1">Total Flights</div>
              <div className="text-2xl font-bold">{flights.length}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-sm text-blue-200 mb-1">From</div>
              <div className="text-xl font-semibold">{from}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-sm text-blue-200 mb-1">To</div>
              <div className="text-xl font-semibold">{to}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-sm text-blue-200 mb-1">Travelers</div>
              <div className="text-xl font-semibold">
                {flightService.calculateTotalPassengers(
                  searchParams?.adults || 1,
                  searchParams?.children || 0,
                  searchParams?.infants || 0
                )} Person(s)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Desktop Filters Sidebar */}
          {showFilters && (
            <div className="hidden lg:block lg:col-span-3">
              <FiltersPanel />
            </div>
          )}

          {/* Flight Results */}
          <div className={`${showFilters ? 'lg:col-span-9' : 'lg:col-span-12'}`}>
            {/* Results Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {filteredFlights.length} flight{filteredFlights.length !== 1 ? 's' : ''} found
                </h2>
                <p className="text-gray-600">
                  Showing flights from {from} to {to}
                </p>
              </div>
              
              <div className="text-sm text-gray-500">
                Sort by: 
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="ml-2 bg-transparent font-medium text-gray-900 focus:outline-none"
                >
                  <option value="price">Price</option>
                  <option value="duration">Duration</option>
                  <option value="departure">Departure Time</option>
                  <option value="arrival">Arrival Time</option>
                  <option value="rating">Rating</option>
                </select>
              </div>
            </div>

            {/* Results List */}
            {filteredFlights.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plane className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No flights found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your filters or search criteria</p>
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reset all filters
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFlights.map((flight, index) => (
                  <FlightCard key={`${flight.id}-${index}`} flight={flight} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightList;